import React, { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { deleteAdminMerchant, fetchAdminMerchant, fetchAdminMerchants, fetchAdminMerchantSignupQr } from '../services/adminApi';
import { FaCheck, FaTimes, FaChartBar, FaEye, FaTrash } from 'react-icons/fa';
import { extractErrorMessage } from '../../../shared/utils/errorHandling';

function StatBadge({ children }) {
    return (
        <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
            {children}
        </span>
    );
}

function YesNo({ value }) {
    const ok = value !== undefined && value !== null && String(value).trim() !== '';
    return (
        <span
            className="inline-flex items-center justify-center w-5"
            title={ok ? 'Yes' : 'No'}
        >
            {ok ? (
                <FaCheck className="h-3.5 w-3.5 text-emerald-600 shrink-0" aria-hidden />
            ) : (
                <FaTimes className="h-3.5 w-3.5 text-slate-400 shrink-0" aria-hidden />
            )}
            <span className="sr-only">{ok ? 'Yes' : 'No'}</span>
        </span>
    );
}

function LocationOk({ ok }) {
    return (
        <span
            className="inline-flex items-center justify-center w-5"
            title={ok ? 'Location OK' : 'Location incomplete'}
        >
            {ok ? (
                <FaCheck className="h-3.5 w-3.5 text-emerald-600 shrink-0" aria-hidden />
            ) : (
                <FaTimes className="h-3.5 w-3.5 text-slate-400 shrink-0" aria-hidden />
            )}
            <span className="sr-only">{ok ? 'Yes' : 'No'}</span>
        </span>
    );
}

function BoolIcon({ value, titleYes = 'Yes', titleNo = 'No' }) {
    const ok = Boolean(value);
    return (
        <span className="inline-flex items-center justify-center w-5" title={ok ? titleYes : titleNo}>
            {ok ? (
                <FaCheck className="h-3.5 w-3.5 text-emerald-600 shrink-0" aria-hidden />
            ) : (
                <FaTimes className="h-3.5 w-3.5 text-slate-400 shrink-0" aria-hidden />
            )}
            <span className="sr-only">{ok ? titleYes : titleNo}</span>
        </span>
    );
}

function parseFilenameFromContentDisposition(header) {
    if (!header || typeof header !== 'string') {
        return null;
    }
    const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
    if (utf8?.[1]) {
        try {
            return decodeURIComponent(utf8[1].trim());
        } catch {
            return utf8[1].trim();
        }
    }
    const quoted = /filename="([^"]+)"/i.exec(header);
    return quoted?.[1] || null;
}

export default function LoyalPassAdminMerchantsPage() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [limit] = useState(25);
    const [q, setQ] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [detail, setDetail] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(null);
    const [confirmName, setConfirmName] = useState('');
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [signupQrLoading, setSignupQrLoading] = useState(false);
    const [signupQrError, setSignupQrError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetchAdminMerchants({ limit, offset, q: q.trim() || undefined });
            setRows(res.data?.items || []);
            setTotal(res.data?.total ?? 0);
        } catch (e) {
            setError(extractErrorMessage(e) || 'Failed to load merchants');
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [limit, offset, q]);

    useEffect(() => {
        load();
    }, [load]);

    const openDetail = async (merchantId) => {
        setDetail({ _loading: true });
        setSignupQrError(null);
        try {
            const res = await fetchAdminMerchant(merchantId);
            setDetail(res.data);
        } catch (e) {
            setDetail({ error: extractErrorMessage(e) || 'Failed to load detail' });
        }
    };

    const closeDetail = () => {
        setDetail(null);
        setSignupQrError(null);
    };

    const openDelete = (row) => {
        setDeleteOpen(row);
        setConfirmName('');
        setDeleteError(null);
    };

    const handleDownloadSignupQr = async () => {
        if (!detail?.merchant?.id) {
            return;
        }
        setSignupQrError(null);
        setSignupQrLoading(true);
        try {
            const res = await fetchAdminMerchantSignupQr(detail.merchant.id);
            const blob = res.data;
            const ct = (res.headers['content-type'] || '').toLowerCase();
            if (!ct.includes('image/png')) {
                const text = await blob.text();
                let msg = 'Could not download QR';
                try {
                    const j = JSON.parse(text);
                    msg = j.message || msg;
                } catch {
                    /* ignore */
                }
                throw new Error(msg);
            }
            const fromHeader = parseFilenameFromContentDisposition(res.headers['content-disposition']);
            const fallback = `${String(detail.merchant.name || 'merchant')
                .trim()
                .replace(/[^a-zA-Z0-9]+/g, '-')}-customer-signup-qr.png`;
            const filename = (fromHeader || fallback).replace(/[^\w.\-]+/g, '-') || 'customer-signup-qr.png';
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.rel = 'noopener';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            setSignupQrError(extractErrorMessage(e) || 'Could not download QR');
        } finally {
            setSignupQrLoading(false);
        }
    };

    const closeDelete = () => {
        if (deleteBusy) {
            return;
        }
        setDeleteOpen(null);
    };

    const submitDelete = async () => {
        if (!deleteOpen?.id) {
            return;
        }
        setDeleteBusy(true);
        setDeleteError(null);
        try {
            await deleteAdminMerchant(deleteOpen.id, confirmName);
            setDeleteOpen(null);
            setDetail(null);
            await load();
        } catch (e) {
            setDeleteError(extractErrorMessage(e) || 'Delete failed');
        } finally {
            setDeleteBusy(false);
        }
    };

    const canPrev = offset > 0;
    const canNext = offset + limit < total;

    const memberCount = (m) => m.stats?.customerLinkCount ?? 0;

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold text-slate-900">Merchants</h1>
                <p className="text-slate-600 mt-1 text-sm">
                    Tiers, enrolled members, rewards, marketing volume, wallet templates, and account deletion. Open a
                    merchant for full details. Open dashboard previews from the Dashboard column.
                </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <input
                    type="search"
                    placeholder="Search by business name or email…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            setOffset(0);
                            load();
                        }
                    }}
                    className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <button
                    type="button"
                    onClick={() => {
                        setOffset(0);
                        load();
                    }}
                    className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90"
                >
                    Search
                </button>
            </div>

            {error && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    {error}
                </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 text-left text-slate-600">
                            <tr>
                                <th className="px-3 py-3 font-medium">Business</th>
                                <th className="px-3 py-3 font-medium">Email</th>
                                <th className="px-3 py-3 font-medium">Tier</th>
                                <th
                                    className="px-3 py-3 font-medium whitespace-nowrap"
                                    title="Customers enrolled in this merchant’s loyalty program (merchant_customers rows)."
                                >
                                    Members
                                </th>
                                <th className="px-3 py-3 font-medium">Rewards</th>
                                <th className="px-3 py-3 font-medium whitespace-nowrap" title="Sum of email_count from marketing sends">
                                    Emails Sent
                                </th>
                                <th className="px-3 py-3 font-medium">Android</th>
                                <th className="px-3 py-3 font-medium">Apple</th>
                                <th className="px-3 py-3 font-medium">Staff</th>
                                <th className="px-3 py-3 font-medium">Created</th>
                                <th className="px-3 py-3 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={11} className="px-4 py-8 text-center text-slate-500">
                                        Loading…
                                    </td>
                                </tr>
                            ) : rows.length === 0 ? (
                                <tr>
                                    <td colSpan={11} className="px-4 py-8 text-center text-slate-500">
                                        No merchants found.
                                    </td>
                                </tr>
                            ) : (
                                rows.map((m) => {
                                    const n = memberCount(m);
                                    return (
                                        <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50/80">
                                            <td className="px-3 py-3 font-medium text-slate-900">{m.name}</td>
                                            <td className="px-3 py-3 text-slate-700 max-w-[140px] truncate" title={m.email}>
                                                {m.email || '—'}
                                            </td>
                                            <td className="px-3 py-3">
                                                <StatBadge>{m.tier || 'free'}</StatBadge>
                                            </td>
                                            <td className="px-3 py-3 tabular-nums">{n}</td>
                                            <td className="px-3 py-3 tabular-nums">{m.stats?.rewardCount ?? 0}</td>
                                            <td className="px-3 py-3 tabular-nums">{m.stats?.marketingEmailsSent ?? 0}</td>
                                            <td className="px-3 py-3">
                                                <YesNo value={m.google_class_id} />
                                            </td>
                                            <td className="px-3 py-3">
                                                <YesNo value={m.apple_class_id} />
                                            </td>
                                            <td className="px-3 py-3 tabular-nums">{m.stats?.staffCount ?? 0}</td>
                                            <td className="px-3 py-3 text-slate-600 whitespace-nowrap">
                                                {m.created_at
                                                    ? format(new Date(m.created_at), 'MMM d, yyyy')
                                                    : '—'}
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap">
                                                <div className="flex flex-wrap items-center gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            navigate(
                                                                `/dashboard/loyalpass-admin/dashboard-previews/${m.id}`
                                                            )
                                                        }
                                                        className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white p-2 text-slate-700 hover:bg-slate-50"
                                                        title="Dashboard preview"
                                                        aria-label="Dashboard preview"
                                                    >
                                                        <FaChartBar className="h-4 w-4" aria-hidden />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => openDetail(m.id)}
                                                        className="inline-flex items-center justify-center rounded-lg p-2 text-primary hover:bg-primary/10"
                                                        title="View merchant details"
                                                        aria-label="View merchant details"
                                                    >
                                                        <FaEye className="h-4 w-4" aria-hidden />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => openDelete(m)}
                                                        className="inline-flex items-center justify-center rounded-lg p-2 text-red-600 hover:bg-red-50"
                                                        title="Delete merchant"
                                                        aria-label="Delete merchant"
                                                    >
                                                        <FaTrash className="h-4 w-4" aria-hidden />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/50 text-sm text-slate-600">
                    <span>
                        Showing {rows.length ? offset + 1 : 0}–{offset + rows.length} of {total}
                    </span>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            disabled={!canPrev || loading}
                            onClick={() => setOffset((o) => Math.max(0, o - limit))}
                            className="rounded-md border border-slate-300 px-3 py-1 disabled:opacity-40"
                        >
                            Previous
                        </button>
                        <button
                            type="button"
                            disabled={!canNext || loading}
                            onClick={() => setOffset((o) => o + limit)}
                            className="rounded-md border border-slate-300 px-3 py-1 disabled:opacity-40"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>

            {detail && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                    role="dialog"
                    aria-modal="true"
                >
                    <div className="max-w-3xl w-full max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
                        <div className="flex justify-between items-start mb-4 gap-3">
                            <h2 className="text-lg font-semibold text-slate-900">Merchant detail</h2>
                            <div className="flex items-center gap-2 shrink-0">
                                {!detail._loading && !detail.error && detail.merchant?.id && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            navigate(
                                                `/dashboard/loyalpass-admin/dashboard-previews/${detail.merchant.id}`
                                            )
                                        }
                                        className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 hover:bg-slate-50"
                                    >
                                        Dashboard preview
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={closeDetail}
                                    className="text-slate-500 hover:text-slate-800 text-xl leading-none"
                                    aria-label="Close"
                                >
                                    ×
                                </button>
                            </div>
                        </div>
                        {detail._loading && <p className="text-slate-600">Loading…</p>}
                        {detail.error && <p className="text-red-600 text-sm">{detail.error}</p>}
                        {!detail._loading && !detail.error && detail.merchant && (
                            <div className="space-y-4 text-sm">
                                <div>
                                    <span className="text-slate-500">ID</span>
                                    <div className="font-mono text-xs break-all">{detail.merchant.id}</div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <span className="text-slate-500">Business name</span>
                                        <div className="font-medium">{detail.merchant.name}</div>
                                    </div>
                                    <div>
                                        <span className="text-slate-500">Program name</span>
                                        <div>{detail.merchant.program_name || '—'}</div>
                                    </div>
                                    <div>
                                        <span className="text-slate-500">Email</span>
                                        <div>{detail.merchant.email || '—'}</div>
                                    </div>
                                    <div>
                                        <span className="text-slate-500">Phone</span>
                                        <div>{detail.merchant.phone || '—'}</div>
                                    </div>
                                    <div>
                                        <span className="text-slate-500">Category</span>
                                        <div>{detail.merchant.category || '—'}</div>
                                    </div>
                                    <div>
                                        <span className="text-slate-500">Website</span>
                                        <div className="break-all">{detail.merchant.website || '—'}</div>
                                    </div>
                                    <div className="sm:col-span-2">
                                        <span className="text-slate-500">Address</span>
                                        <div>{detail.merchant.address || '—'}</div>
                                    </div>
                                    <div className="sm:col-span-2">
                                        <span className="text-slate-500">Location OK</span>
                                        <div>
                                            <LocationOk ok={!!detail.locationSetCorrectly} />
                                        </div>
                                        <p className="text-xs text-slate-500 mt-1">
                                            A check when the business address is filled and at least 5 characters (same
                                            minimum as General Settings). List view does not geocode each row.
                                        </p>
                                    </div>
                                </div>

                                <div className="pt-2 border-t border-slate-100">
                                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                                        Tier &amp; billing
                                    </h3>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <span className="text-slate-500">Tier</span>
                                            <div>
                                                <StatBadge>{detail.merchant.tier}</StatBadge>
                                            </div>
                                        </div>
                                        <div>
                                            <span className="text-slate-500">Tutorial done</span>
                                            <div>
                                                <BoolIcon
                                                    value={detail.merchant.tutorial_completed}
                                                    titleYes="Tutorial completed"
                                                    titleNo="Tutorial not completed"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-span-2">
                                            <span className="text-slate-500">Stripe customer</span>
                                            <div className="font-mono text-xs break-all">
                                                {detail.merchant.stripe_customer_id || '—'}
                                            </div>
                                        </div>
                                        <div className="col-span-2">
                                            <span className="text-slate-500">Stripe subscription</span>
                                            <div className="font-mono text-xs break-all">
                                                {detail.merchant.stripe_subscription_id || '—'}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-2 border-t border-slate-100">
                                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                                        Wallet templates
                                    </h3>
                                    <div className="grid grid-cols-1 gap-2">
                                        <div>
                                            <span className="text-slate-500">Apple Wallet (pass type) class</span>
                                            <div>
                                                {detail.merchant.apple_class_id ? (
                                                    <span className="font-mono text-xs break-all">
                                                        {detail.merchant.apple_class_id}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-400">Not created</span>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <span className="text-slate-500">Google Wallet class ID</span>
                                            <div>
                                                {detail.merchant.google_class_id ? (
                                                    <span className="font-mono text-xs break-all">
                                                        {detail.merchant.google_class_id}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-400">Not created</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-2 border-t border-slate-100">
                                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                                        In-store customer signup
                                    </h3>
                                    <p className="text-xs text-slate-600 mb-2">
                                        Printable QR for the public customer form (same link as in Settings → General).
                                    </p>
                                    <button
                                        type="button"
                                        onClick={handleDownloadSignupQr}
                                        disabled={signupQrLoading}
                                        className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        {signupQrLoading ? 'Preparing…' : 'Download signup QR (PNG)'}
                                    </button>
                                    {signupQrError && (
                                        <p className="mt-2 text-xs text-red-600">{signupQrError}</p>
                                    )}
                                </div>

                                <div className="pt-2 border-t border-slate-100">
                                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                                        Reward program settings
                                    </h3>
                                    {detail.rewardSettings ? (
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <span className="text-slate-500">Points per $</span>
                                                <div className="font-medium">
                                                    {detail.rewardSettings.point_per_dollar ?? '—'}
                                                </div>
                                            </div>
                                            <div>
                                                <span className="text-slate-500">Points enabled</span>
                                                <div>
                                                    <BoolIcon value={detail.rewardSettings.points_enabled} />
                                                </div>
                                            </div>
                                            {/* Visits + birthday fields omitted in admin until LoyalPass ships them */}
                                        </div>
                                    ) : (
                                        <p className="text-slate-500">No reward settings row.</p>
                                    )}
                                </div>

                                <div className="pt-2 border-t border-slate-100">
                                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                                        Usage
                                    </h3>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <span className="text-slate-500">Enrolled members</span>
                                            <div className="tabular-nums font-medium">
                                                {detail.stats?.customerLinkCount ?? 0}
                                            </div>
                                        </div>
                                        <div>
                                            <span className="text-slate-500">Rewards configured</span>
                                            <div className="tabular-nums">{detail.stats?.rewardCount ?? 0}</div>
                                        </div>
                                        <div>
                                            <span className="text-slate-500">Marketing emails sent (total)</span>
                                            <div className="tabular-nums">{detail.stats?.marketingEmailsSent ?? 0}</div>
                                        </div>
                                        <div>
                                            <span className="text-slate-500">Staff seats</span>
                                            <div className="tabular-nums">{detail.stats?.staffCount ?? 0}</div>
                                        </div>
                                    </div>
                                </div>

                                {detail.authUser && (
                                    <div className="pt-2 border-t border-slate-100">
                                        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                                            Auth account
                                        </h3>
                                        <div className="space-y-1 text-xs">
                                            <div>Confirmed: {detail.authUser.email_confirmed_at || '—'}</div>
                                            <div>Last sign-in: {detail.authUser.last_sign_in_at || '—'}</div>
                                            <div>Created: {detail.authUser.created_at || '—'}</div>
                                        </div>
                                    </div>
                                )}

                                <button
                                    type="button"
                                    onClick={() => {
                                        openDelete(detail.merchant);
                                    }}
                                    className="mt-4 w-full rounded-lg border border-red-200 bg-red-50 py-2 text-sm font-medium text-red-700 hover:bg-red-100"
                                >
                                    Delete this merchant…
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {deleteOpen && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
                    role="dialog"
                    aria-modal="true"
                >
                    <div className="max-w-md w-full rounded-xl bg-white p-6 shadow-xl">
                        <h2 className="text-lg font-semibold text-red-800">Delete merchant</h2>
                        <p className="mt-2 text-sm text-slate-600">
                            This removes the merchant row, passes, customers linked only to this store, staff auth
                            users, rewards, and the owner&apos;s Supabase auth account. Type the business name
                            exactly: <span className="font-semibold text-slate-900">{deleteOpen.name}</span>
                        </p>
                        <input
                            type="text"
                            value={confirmName}
                            onChange={(e) => setConfirmName(e.target.value)}
                            className="mt-4 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                            placeholder="Business name"
                            autoComplete="off"
                        />
                        {deleteError && (
                            <p className="mt-2 text-sm text-red-600">{deleteError}</p>
                        )}
                        <div className="mt-6 flex gap-2 justify-end">
                            <button
                                type="button"
                                onClick={closeDelete}
                                disabled={deleteBusy}
                                className="rounded-lg border border-slate-300 px-4 py-2 text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={submitDelete}
                                disabled={deleteBusy}
                                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                            >
                                {deleteBusy ? 'Deleting…' : 'Delete permanently'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
